import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import UnderlineInput from './UnderlineInput';
import { MENU_CLASS, normalizeText, optionClass, useAnchoredPosition, useOutsideClose } from './dropdownKit';
import { createPaymentEntry, searchPaymentEntries } from './entryApi';

// "HSBC01VND · HSBC - HoChiMinh City VND": the code stands out, the name follows.
const OptionLabel = ({ row }) => {
  const label = String(row.label ?? row.value ?? '');
  const prefix = `${row.value} · `;
  if (!row.value || !label.startsWith(prefix)) return <span className="tw-break-words tw-min-w-0">{label}</span>;
  return (
    <span className="tw-min-w-0 tw-break-words">
      <span className="tw-font-semibold tw-text-geist-fg">{row.value}</span>
      <span className="tw-text-geist-fg-2"> · {label.slice(prefix.length)}</span>
    </span>
  );
};

OptionLabel.propTypes = { row: PropTypes.object.isRequired };

const canonical = (kind, text) => normalizeText(text).trim().replace(/\s+/g, ' ')
  .replace(kind === 'bank_account' ? /[\s-]/g : /$^/g, '');

/**
 * Editable, server-searched directory. Only committed new text is saved; keystrokes never create rows.
 * AX-backed kinds (accounts, dimensions) answer `catalog: 'ax'`: once the AX copy is in (`synced`)
 * the list holds only codes AX accepts, so nothing typed is added and a code AX lacks is flagged.
 */
const EntryPicker = React.forwardRef(function EntryPicker({
  kind, value = '', onChange, onSelect, onBlur, context = {}, paymentRequestId, disabled = false,
  cell = false, className = '', placeholder = 'Search or enter a new value…', ...inputProps
}, forwardedRef) {
  const inputRef = useRef(null), menuRef = useRef(null), savingRef = useRef(false);
  const latestValue = useRef(value);
  latestValue.current = value;
  const id = useId();
  const listId = `entry-list-${id}`;
  const [open, setOpen] = useState(false), [rows, setRows] = useState([]), [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [active, setActive] = useState(0), [retry, setRetry] = useState(0), [catalog, setCatalog] = useState(null);
  const contextKey = JSON.stringify(context);
  // Wider than the cell: AX names are long ("HSBC - HoChiMinh City VND - Guarantee").
  const coords = useAnchoredPosition(open, inputRef, { minWidth: 360, maxHeight: 320 });
  useOutsideClose(open, [inputRef, menuRef], () => setOpen(false));

  useEffect(() => {
    if (!open || disabled) return undefined;
    const controller = new AbortController();
    let alive = true;
    setLoading(true); setError(''); setRows([]); setActive(0);
    const timer = setTimeout(() => {
      searchPaymentEntries({ kind, q: value, context: JSON.parse(contextKey), payment_request_id: paymentRequestId }, controller.signal)
        .then((response) => {
          if (!alive) return;
          setRows(response.data || []); setTotal(response.total || 0);
          setCatalog(response.catalog === 'ax' ? { synced: !!response.synced } : null);
        })
        .catch((e) => { if (alive && !controller.signal.aborted) setError(e.response?.data?.error || 'Could not load the entry list.'); })
        .finally(() => { if (alive) setLoading(false); });
    }, 180);
    return () => { alive = false; clearTimeout(timer); controller.abort(); };
  }, [open, disabled, kind, value, contextKey, paymentRequestId, retry]);

  const exact = rows.find((row) => canonical(kind, row.value) === canonical(kind, value));
  const axOnly = !!catalog?.synced;
  const notInAx = axOnly && String(value).trim() && !exact && !loading && !error;
  const canCreate = String(value).trim() && !exact && !loading && !error && !axOnly;
  const heading = axOnly ? 'AX values · search by code or name'
    : catalog ? 'AX list is loading · showing values typed before'
      : 'Search existing entries or type a new value';
  const count = rows.length + (canCreate ? 1 : 0);
  const commit = async (text = value) => {
    if (!String(text).trim() || disabled || savingRef.current || loading || error || exact || axOnly) return;
    if (kind === 'bank_account' && (!context.payee?.trim() || !context.bank_name?.trim())) {
      setNotice('');
      return;
    }
    savingRef.current = true;
    setNotice('Adding to entry list…');
    try {
      const entry = await createPaymentEntry({ kind, value: text, context, payment_request_id: paymentRequestId });
      if (latestValue.current === text) { onChange(entry.value); onSelect?.(entry); setNotice('Added to entry list.'); }
    } catch (e) {
      setNotice(e.response?.data?.error || 'Could not add to the list. Your typed value is retained.');
    } finally { savingRef.current = false; }
  };
  const pick = (index) => {
    if (index < rows.length) {
      const row = rows[index];
      onChange(row.value); onSelect?.(row); setNotice('');
    } else if (canCreate) { commit(); }
    setOpen(false);
    inputRef.current?.focus();
  };
  const keyDown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); setOpen(true);
      setActive((index) => Math.max(0, Math.min(count - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
    }
    if (event.key === 'Enter' && open) { event.preventDefault(); if (count && !loading && !error) pick(active); }
  };
  useEffect(() => { if (open) menuRef.current?.querySelector(`[data-entry-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' }); }, [active, open]);

  return <>
    <UnderlineInput {...inputProps} ref={(node) => {
      inputRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node); else if (forwardedRef) forwardedRef.current = node;
    }} role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={open ? listId : undefined}
      aria-activedescendant={open && count ? `${listId}-${active}` : undefined}
      value={value} disabled={disabled} cell={cell} className={className} placeholder={placeholder}
      onFocus={() => { if (!disabled) setOpen(true); }}
      onChange={(event) => { onChange(event.target.value); setNotice(''); setOpen(true); }}
      onBlur={(event) => { setOpen(false); commit(); onBlur?.(event); }} onKeyDown={keyDown} />
    {notice && <span role="status" className="tw-block tw-text-[11px] tw-text-geist-fg-3 tw-mt-1 tw-break-words">{notice}</span>}
    {open && coords && createPortal(<div ref={menuRef} className={MENU_CLASS}
      style={{ position: 'fixed', zIndex: 100060, ...coords, maxHeight: undefined, width: Math.min(coords.width, window.innerWidth - 16) }}>
      <div className="tw-px-3 tw-py-1.5 tw-text-[11px] tw-text-geist-fg-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">{heading}</div>
      {loading && <div role="status" className="tw-p-3 tw-text-sm tw-text-geist-fg-3">Searching…</div>}
      {error && <div role="alert" className="tw-p-3 tw-text-sm tw-text-geist-fg-3">{error} <button type="button"
        onMouseDown={(event) => event.preventDefault()} onClick={() => setRetry((n) => n + 1)}>Retry</button></div>}
      <div id={listId} role="listbox" aria-label="Entry suggestions" style={{ maxHeight: coords.maxHeight }} className="tw-overflow-y-auto">
        {rows.map((row, index) => <div key={row.id} id={`${listId}-${index}`} data-entry-index={index}
          role="option" aria-selected={index === active} className={optionClass(false, index === active)}
          onMouseDown={(event) => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => pick(index)}>
          <OptionLabel row={row} />
        </div>)}
        {canCreate && <div id={`${listId}-${rows.length}`} data-entry-index={rows.length} role="option" aria-selected={active === rows.length}
          className={optionClass(false, active === rows.length)} onMouseDown={(event) => event.preventDefault()}
          onMouseEnter={() => setActive(rows.length)} onClick={() => pick(rows.length)}>
          <span className="tw-break-words tw-min-w-0">Use “{value}” · add to entry list</span>
        </div>}
      </div>
      {notInAx && <div role="status" className="tw-px-3 tw-py-2 tw-text-[12px] tw-text-geist-warning tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
        “{value}” is not in AX — ERP will refuse it. Pick a value from the list.</div>}
      {!loading && !error && !count && !notInAx && <div className="tw-p-3 tw-text-sm tw-text-geist-fg-3">
        {axOnly ? 'No AX values match.' : 'No entries yet. Type to add one.'}</div>}
      {!loading && !error && total > rows.length && <div className="tw-px-3 tw-py-2 tw-text-[11px] tw-text-geist-fg-3">Showing {rows.length} of {total} · type more to narrow the search</div>}
    </div>, document.body)}
  </>;
});

EntryPicker.propTypes = {
  kind: PropTypes.string.isRequired, value: PropTypes.string, onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func, onBlur: PropTypes.func, context: PropTypes.object,
  paymentRequestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), disabled: PropTypes.bool,
  cell: PropTypes.bool, className: PropTypes.string, placeholder: PropTypes.string,
};

export default EntryPicker;
